// The per-map builder contract behind `createMapView` (§12.5). Each arena builds its own
// geometry, hazards and telegraphs; MapView adds what every map shares (sky, fog band, void,
// audience, confetti, splash rings, debris, shadow fitting) and merges static props per material.
import type { ColorRepresentation, IUniform, Material, Object3D, Texture, Vector3 } from 'three';
import type { SimEvent } from '@shared/events';
import type { MapId } from '@shared/ids';
import type { FrameView } from '../../../game/FrameView';
import type { MaterialLibrary } from '../../materials/library';
import type { QualityTier } from '../../Renderer';
import type { AudienceLayout } from '../env/audience';
import type { SkyStyle } from '../env/sky';
import type { VoidColors, VoidShadow } from '../env/void';
import type { Batch } from '../kit/geometry';
import type { EnvUniforms } from '../kit/materials';
import type { MapParticles } from '../kit/particles';
import type { MapPalette } from '../MapView';
import type { MapProbe } from '../probe';

/** Static batch buckets (one material each). */
export const B = {
  /** Arena props: vertex-colored toon, cast and receive shadows. */
  toon: 'toon',
  /** Glossy arena props (porcelain, enamel, plastic). */
  gloss: 'gloss',
  /** Unlit emissive (neon, lamps). */
  glow: 'glow',
  /** Prop outlines (0.035 m) and bold arena-rim outlines (0.055 m). */
  hull: 'hull',
  hullBold: 'hullBold',
  /** Background props in the fog band: toon, no shadows, no outlines on Low/Potato. */
  bg: 'bg',
  bgHull: 'bgHull',
  bgGlow: 'bgGlow',
  /** Polished metal mirroring the map's painted environment (spoon, forks, drum steel). */
  chrome: 'chrome',
} as const;

export interface ArenaKit {
  readonly mapId: MapId;
  readonly seed: number;
  readonly tier: QualityTier;
  readonly lib: MaterialLibrary;
  readonly palette: MapPalette;
  readonly env: EnvUniforms;
  readonly ramp: Texture;
  readonly outlineScale: IUniform<number>;
  readonly particles: MapParticles;
  readonly probe: MapProbe;
  readonly sunDir: Vector3;
  /** World-fixed static props (merged by MapView). */
  readonly batch: Batch;
  /** The shared bucket materials, for arena groups that merge their own batches. */
  readonly mats: Readonly<Record<'toon' | 'gloss' | 'glow' | 'hull' | 'hullBold' | 'bg' | 'bgGlow' | 'chrome', Material>>;
  /** The map's painted environment (equirect) for chrome and glossy metal. */
  readonly envMap: Texture;
  /** Segment count helper: `hi` on High, scaled down per tier (min `lo`). */
  seg(hi: number, lo?: number): number;
  /** True on High and Medium. */
  readonly rich: boolean;
}

export interface ArenaFrame {
  readonly view: FrameView;
  /** Presentation seconds (slowed by replay time scale). */
  readonly time: number;
  readonly dt: number;
  readonly beat: number;
}

export interface ArenaLayout {
  sky: SkyStyle;
  voidColors: VoidColors;
  /** Void foam/edge ring radius around the arena center. */
  voidEdge: number;
  /** The arena footprint's contact shadow on the void (default: a disc of `voidEdge`). */
  voidShadow?: VoidShadow;
  /** A churning foam spot on the void (sim x, y, radius). */
  voidPour?: { x: number; y: number; r: number };
  splash: { ring: ColorRepresentation; edge: ColorRepresentation };
  audience: AudienceLayout;
  confetti: { colors: readonly ColorRepresentation[]; r0: number; r1: number; y0: number; y1: number };
  /** Height range (three.js y) of shadow casters over the arena, for frustum fitting. */
  shadowY: readonly [number, number];
}

export interface Arena {
  readonly root: Object3D;
  readonly layout: ArenaLayout;
  update(f: ArenaFrame): void;
  onEvent(ev: SimEvent, f: ArenaFrame): void;
  /** Visual ground offset at sim (x, y): the tilted Teacup saucer; 0 elsewhere. */
  surfaceOffset?(x: number, y: number): number;
  /** Debug: rendered hazard transforms for the alignment check. */
  debugHazards?(): DebugHazard[];
  dispose(): void;
}

/** A rendered hazard's centerline in world space (three.js), for the sweeper alignment check. */
export interface DebugHazard {
  kind: 'arm' | 'flipper' | 'ball' | 'bubble' | 'cup';
  index: number;
  /** Two world points (three.js) along the rendered capsule axis (or the center twice). */
  a: Vector3;
  b: Vector3;
  radius: number;
}

export type ArenaBuilder = (kit: ArenaKit) => Arena;
